import {
    GridItem,
    NumberField,
    CheckboxField,
    CprField,
    validateAccountNumber,
    validateBankNumber,
    validateCpr,
    Grid,
    CvrField,
    validateCvr
} from '@flos/react-ui';
import { type ChangeEvent, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import StringTitle from '../components/StringTitle';
import { updatedPaymentInformationAtom } from '../../../recoil/atoms/updatedPaymentInformationAtom';
import { bankAccountValidityStatusAtom } from '../../../recoil/atoms/bankAccountValidityStatusAtom';
import { useValidateBankAccountData } from '../../../recoil/hooks/useValidateBankAccountData';
import { hasCoAccountHolderAtom } from '../../../recoil/atoms/hasCoAccountHolderAtom';
import { useHippoText } from '../../../hooks/useHippoText';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import type { UpdatePaymentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/payment/';
import type { BankAccountFieldProps } from '../../../recoil/types/types';

const BankAccountFields = ({
    paymentInfo,
    setPaymentInfo
}: {
    paymentInfo: BankAccountFieldProps;
    setPaymentInfo: React.Dispatch<React.SetStateAction<BankAccountFieldProps>>;
}): React.JSX.Element => {
    const { validateBankAccountData } = useValidateBankAccountData();
    const [isBankAccountValid, setIsBankAccountValid] = useRecoilState(
        bankAccountValidityStatusAtom
    );
    const [updatedPaymentInfo, setUpdatedPaymentInfo] = useRecoilState(
        updatedPaymentInformationAtom
    );
    const { cpr, cvr } = updatedPaymentInfo;
    const [hasCoAccountHolder, setHasCoAccountHolder] = useRecoilState(hasCoAccountHolderAtom);
    const [accountHolderData] = useRecoilState(accountHolderAtom);
    const { isCVR } = accountHolderData;
    const { accountNumber = '', bankReference = '' } = paymentInfo;
    const getHippoText = useHippoText();
    const hippoText = {
        fieldsTitle: getHippoText('DSS-PAYMENT-INFO-SCREEN-REGISTER-BS'),
        registrationNo: getHippoText('DSS-PAYMENT-INFO-SCREEN-REG-NUMBER'),
        accountNo: getHippoText('DSS-PAYMENT-INFO-SCREEN-ACCOUNT-NUMBER'),
        onlyMyName: getHippoText('DSS-PAYMENT-INFO-SCREEN-ONLY-MY-NAME'),
        coHolder: getHippoText('DSS-PAYMENT-INFO-SCREEN-CO-HOLDER'),
        onlyMyCompany: getHippoText('DSS-PAYMENT-INFO-SCREEN-ONLY-MY-CVR'),
        coHolderCvr: getHippoText('DSS-PAYMENT-INFO-SCREEN-CO-HOLDER-CVR')
    };

    const handleAccountHolderFieldChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;
        setPaymentInfo((prev) => {
            return { ...prev, [name]: value };
        });

        if (name === 'bankReference') {
            validateAccountHolderFields(value, accountNumber);
        } else if (name === 'accountNumber') {
            validateAccountHolderFields(bankReference, value);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;
        setUpdatedPaymentInfo((prevFormData: UpdatePaymentDTO) => ({
            ...prevFormData,
            [name]: value.replace(/[\s]/g, '')
        }));
    };

    const validateAccountHolderFields = useCallback(
        (registrationNum: string, accountNum: string) => {
            if (registrationNum.length === 4 && accountNum.length >= 4) {
                void validateBankAccountData(registrationNum, accountNum).then((response) => {
                    setIsBankAccountValid(response?.valid ?? false);
                });
            }
        },
        [setIsBankAccountValid, validateBankAccountData]
    );

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.checked;
        setHasCoAccountHolder(!value);
        if (value) {
            setUpdatedPaymentInfo((prev: UpdatePaymentDTO) => ({
                ...prev,
                cpr: undefined,
                cvr: undefined
            }));
        }
    };

    const isBankNrValid = bankReference
        ? validateBankNumber(bankReference) && isBankAccountValid
        : true;

    const isAccountNrValid = accountNumber
        ? validateAccountNumber(accountNumber) && isBankAccountValid
        : true;

    const isCprNrValid = cpr ? validateCpr(cpr?.replace(/-/g, '')) : true;

    const bankAccountErrorMsg = !isBankAccountValid ? 'Invalid Betalingsservice information' : '';

    return (
        <>
            <GridItem className="u-spacing-stack-top-4" span={12}>
                <StringTitle>{hippoText.fieldsTitle}</StringTitle>
                <Grid className="u-spacing-stack-top-4 u-gap-y-none">
                    <GridItem span={3}>
                        <NumberField
                            label={hippoText.registrationNo}
                            name="bankReference"
                            value={bankReference}
                            maxLength={4}
                            isValid={isBankNrValid}
                            onChange={handleAccountHolderFieldChange}
                            allowLeadingZeros
                            errorText=""
                        />
                    </GridItem>
                    <GridItem span={9}>
                        <NumberField
                            label={hippoText.accountNo}
                            name="accountNumber"
                            value={accountNumber}
                            minLength={4}
                            maxLength={10}
                            isValid={isAccountNrValid}
                            onChange={handleAccountHolderFieldChange}
                            allowLeadingZeros
                            errorText={bankAccountErrorMsg}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
            <GridItem span={12}>
                <CheckboxField
                    label={isCVR ? hippoText.onlyMyCompany : hippoText.onlyMyName}
                    onChange={handleCheckboxChange}
                    checked={!hasCoAccountHolder}
                />
                {hasCoAccountHolder && !isCVR && (
                    <CprField
                        name="cpr"
                        value={cpr}
                        onBlur={handleInputChange}
                        isValid={isCprNrValid}
                        label={hippoText.coHolder}
                    />
                )}

                {hasCoAccountHolder && isCVR && (
                    <CvrField
                        name="cvr"
                        value={cvr}
                        onBlur={handleInputChange}
                        isValid={validateCvr(cvr ?? '')}
                        label={hippoText.coHolderCvr}
                    />
                )}
            </GridItem>
        </>
    );
};

export default BankAccountFields;
